import { useEffect, useState } from 'react';
import { Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TextBausteinPicker, type TextBausteinKategorie } from '@/components/text-baustein-picker';
import { AIRewriteButton } from '@/components/ui/ai-rewrite-button';

interface GrosstextDialogProps {
  /** Aktueller Wert des Textfeldes. */
  value: string;
  /** Wird beim Klick auf "Übernehmen" mit dem bearbeiteten Text aufgerufen. */
  onChange: (value: string) => void;
  /** Stabile Dokument-/Feld-ID für asynchrone KI-Rewrites. */
  scopeKey: string;
  /** Titel des Dialogs, z.B. "Vortext bearbeiten". */
  label: string;
  /** Kategorie für den TextBausteinPicker. Ohne Angabe wird der Picker nicht angezeigt. */
  kategorie?: TextBausteinKategorie;
  /** Gewerk-Kontext für den AIRewriteButton. */
  gewerk?: string;
  /** Deaktiviert den Auslöse-Button. */
  disabled?: boolean;
  className?: string;
}

export function GrosstextDialog({
  value,
  onChange,
  scopeKey,
  label,
  kategorie,
  gewerk,
  disabled = false,
  className = '',
}: GrosstextDialogProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value ?? '');

  // Bei jedem Öffnen den Entwurf mit dem aktuellen Feldwert initialisieren,
  // damit externe Änderungen (z.B. via Textbaustein/KI im schmalen Feld) nicht überschrieben werden.
  useEffect(() => {
    if (open) {
      setDraft(value ?? '');
    }
  }, [open, value]);

  const handleApply = () => {
    onChange(draft);
    setOpen(false);
  };

  const handleCancel = () => {
    setDraft(value ?? '');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : handleCancel())}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={`h-7 w-7 ${className}`}
        disabled={disabled}
        onClick={() => setOpen(true)}
        title="Groß bearbeiten"
      >
        <Maximize2 className="h-3.5 w-3.5" />
        <span className="sr-only">Groß bearbeiten</span>
      </Button>

      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
          <DialogDescription>
            Text in großer Ansicht bearbeiten. Änderungen werden erst mit „Übernehmen" gespeichert.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1.5">
          <AIRewriteButton
            text={draft}
            onRewritten={(rewritten) => setDraft(rewritten)}
            scopeKey={`${scopeKey}:dialog`}
            context={gewerk ? { gewerk } : undefined}
          />
          {kategorie && (
            <TextBausteinPicker
              kategorie={kategorie}
              currentValue={draft}
              onInsert={(next) => setDraft(next)}
            />
          )}
        </div>

        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="min-h-[60vh] w-full font-mono text-sm resize-y"
          placeholder={label}
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleCancel}>
            Abbrechen
          </Button>
          <Button type="button" onClick={handleApply}>
            Übernehmen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default GrosstextDialog;
